import { useEffect, useCallback, useMemo, useState } from 'react'
import { Box, Button, Flex, Image, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { LuRefreshCw } from 'react-icons/lu'

import IconClose from '@/components/icons/IconClose'
import { useBrandContext } from '@/context/brand-context'
import { downloadFromUrl } from '@/utils/download'
import DesignMetadataPanel from './design-metadata-panel'
import EditDesignModal from './edit-design-modal'
import '@/components/ambient/animations.css'

import type { Design } from '@/api/designs'

interface LightboxProps {
  design: Design
  onClose: () => void
  onRedesignComplete: () => void
}

export default function Lightbox({ design, onClose, onRedesignComplete }: LightboxProps) {
  const imageUrl = design.imageUrl ?? ''
  const [redesignOpen, setRedesignOpen] = useState(false)

  const { brandAnalysis } = useBrandContext()
  const brandColor = useMemo(() => {
    const colors = brandAnalysis?.brand.colors ?? []
    return (
      colors.find((c) => c.usage === 'primary')?.hex ?? colors[0]?.hex ?? null
    )
  }, [brandAnalysis?.brand.colors])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !redesignOpen) onClose()
    },
    [onClose, redesignOpen]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [handleKeyDown])

  const handleDownload = () => {
    if (!imageUrl) return
    void downloadFromUrl(imageUrl, `ablo-design-${Date.now()}`)
  }

  const handleRedesignComplete = () => {
    setRedesignOpen(false)
    onRedesignComplete()
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(8, 10, 14, 0.9)',
          backdropFilter: 'blur(8px)',
          overflow: 'auto'
        }}
        onClick={onClose}
      >
        {/* Close button */}
        <Box
          position="fixed"
          top="20px"
          right="20px"
          w="34px"
          h="34px"
          borderRadius="full"
          bg="rgba(20, 23, 30, 0.8)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.1)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          transition="all 0.2s ease"
          _hover={{ bg: 'rgba(34, 39, 50, 0.85)' }}
          onClick={onClose}
          zIndex={1001}
        >
          <IconClose color="text.primary" />
        </Box>

        {/* Download button */}
        <Box
          position="fixed"
          top="20px"
          right="64px"
          h="34px"
          px="12px"
          borderRadius="full"
          bg="rgba(20, 23, 30, 0.8)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.1)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          transition="all 0.2s ease"
          _hover={{ bg: 'rgba(34, 39, 50, 0.85)' }}
          onClick={(e) => {
            e.stopPropagation()
            handleDownload()
          }}
          zIndex={1001}
        >
          <Text color="whiteAlpha.800" textStyle="bodySuperSmall">
            Download
          </Text>
        </Box>

        {/* Content: Image + Metadata */}
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            gap={{ base: '14px', lg: '24px' }}
            alignItems={{ base: 'center', lg: 'stretch' }}
            maxW="97vw"
            maxH={{ base: 'none', lg: '85vh' }}
          >
            <Image
              src={imageUrl}
              alt={design.name}
              maxW={{ base: '90vw', lg: '65vw' }}
              maxH={{ base: '60vh', lg: '85vh' }}
              objectFit="contain"
              borderRadius="10px"
              border="1px solid"
              borderColor="rgba(255,255,255,0.08)"
              flexShrink={0}
            />

            <Box
              w={{ base: '90vw', lg: '380px', xl: '420px', '2xl': '460px' }}
              flexShrink={0}
              py={{ base: '0', lg: '2px' }}
              display="flex"
              flexDirection="column"
              overflowY="auto"
              css={{
                '&::-webkit-scrollbar': { width: '0px' }
              }}
            >
              <DesignMetadataPanel design={design} />

              {design.imageUrl && (
                <Box
                  position="sticky"
                  bottom="0"
                  pt="12px"
                  pb="2px"
                  mt="auto"
                  flexShrink={0}
                  bg="linear-gradient(to bottom, transparent, rgba(8, 10, 14, 0.95) 30%)"
                >
                  <Button
                    w="100%"
                    h="40px"
                    px="20px"
                    textStyle="bodySmall"
                    fontWeight={500}
                    borderRadius="9999px"
                    bg={brandColor ?? 'accent.500'}
                    color="white"
                    transition="all 0.3s ease"
                    _hover={{
                      opacity: 0.85,
                      transform: 'translateY(-1px)',
                    }}
                    onClick={() => setRedesignOpen(true)}
                  >
                    <Flex align="center" gap="6px">
                      <LuRefreshCw size={14} />
                      Edit Design
                    </Flex>
                  </Button>
                </Box>
              )}
            </Box>
          </Flex>
        </motion.div>
      </motion.div>

      {redesignOpen && (
        <EditDesignModal
          design={design}
          isOpen={redesignOpen}
          onClose={() => setRedesignOpen(false)}
          onComplete={handleRedesignComplete}
        />
      )}
    </AnimatePresence>
  )
}
